import type { User, LoginCredentials, AuthResponse } from '@nomiss/types';
import type { GenerateOtpDto, ValidateOtpDto } from '@nomiss/sdk';
import { ResponseError } from '@nomiss/sdk';
import {
  API_ENDPOINTS,
  COOKIE_NAMES,
  LOCAL_STORAGE_KEYS,
} from '@nomiss/constants';
import { executeSdkRawRequest, unwrapApiData } from './api-request.util';
import { apiService } from './api.service';
import { otpApi } from './sdk-client';
import { storageService } from './storage.service';

/**
 * Authentication Service
 */
class AuthService {
  /**
   * Request OTP for a phone number
   */
  async generateOtp(payload: GenerateOtpDto): Promise<number | null> {
    const data = await executeSdkRawRequest<{
      sent?: boolean;
      expiresInSeconds?: number;
      otp?: number;
    }>({
      request: () =>
        otpApi.otpControllerGenerateOtpV1Raw({
          generateOtpDto: payload,
        }),
      fallback: 'Failed to generate OTP',
    });

    if (typeof data?.otp === 'number') {
      return data.otp;
    }

    return null;
  }

  /**
   * Validate OTP for a phone number
   */
  async validateOtp(payload: ValidateOtpDto): Promise<boolean> {
    try {
      const raw = await otpApi.otpControllerValidateOtpV1Raw({
        validateOtpDto: payload,
      });

      if (!raw.raw.ok) {
        return false;
      }

      const json = await raw.raw.json();
      const data = await unwrapApiData<boolean>(json);
      return data === true;
    } catch (error) {
      if (error instanceof ResponseError) {
        return false;
      }

      throw error;
    }
  }

  /**
   * Login user
   */
  async login(email: string, password: string): Promise<AuthResponse | null> {
    const credentials: LoginCredentials = { email, password };

    const response = await apiService.post<AuthResponse>(
      API_ENDPOINTS.AUTH_LOGIN,
      credentials,
    );

    if (response.success && response.data) {
      this.setAuthData(response.data);
      return response.data;
    }

    return null;
  }

  /**
   * Logout user
   */
  async logout(deviceId?: string): Promise<void> {
    await apiService.post(
      API_ENDPOINTS.AUTH_LOGOUT,
      deviceId ? { deviceId } : undefined,
    );
    this.clearAuthData();
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    const userJson = storageService.get(LOCAL_STORAGE_KEYS.USER);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  /**
   * Get auth token
   */
  getAuthToken(): string | null {
    return storageService.get(COOKIE_NAMES.AUTH_TOKEN);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  /**
   * Set authentication data
   */
  private setAuthData(authData: AuthResponse): void {
    storageService.set(COOKIE_NAMES.AUTH_TOKEN, authData.token);
    storageService.set(COOKIE_NAMES.REFRESH_TOKEN, authData.refreshToken);
    storageService.set(LOCAL_STORAGE_KEYS.USER, JSON.stringify(authData.user));

    apiService.setAuthToken(authData.token);
  }

  /**
   * Clear authentication data
   */
  private clearAuthData(): void {
    storageService.remove(COOKIE_NAMES.AUTH_TOKEN);
    storageService.remove(COOKIE_NAMES.REFRESH_TOKEN);
    storageService.remove(LOCAL_STORAGE_KEYS.USER);

    apiService.removeAuthToken();
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<boolean> {
    const refreshToken = storageService.get(COOKIE_NAMES.REFRESH_TOKEN);
    if (!refreshToken) return false;

    const response = await apiService.post<AuthResponse>(
      API_ENDPOINTS.AUTH_REFRESH,
      { refreshToken },
    );

    if (response.success && response.data) {
      this.setAuthData(response.data);
      return true;
    }

    this.clearAuthData();
    return false;
  }
}

export const authService = new AuthService();
